import EyeIcon from '@/components/_common/EyeIcon';
import StatusTag from '@/components/_common/StatusTag';
import {
  Button,
  Card,
  Col,
  Divider,
  Popover,
  Row,
  Statistic,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import moment from 'moment';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import ReceiptIcon from '@/components/_common/ReceiptIcon';
import formatterPrice from '@/utils/number/formatterPrice';
import { Group } from '@/components/_common';
import { useStoneDeviceList } from '@/core/domain/stone/devices/devices.hooks';
import { useCallback, useMemo, useState } from 'react';
import EmployeePopoverGenerateReceipt from '@/components/_common/EmployeePopoverGenerateReceipt';
import { Destination } from '@/core/domain/financial/BankingEntry/BankingEntry.types';
import AntdFeatherIcon from '@/components/_common/AntdFeatherIcon';
import { Info } from 'react-feather';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import useReportGenerate, { ReportCallback } from '@/hooks/useReportGenerate';
import { WorkOrderFinancialReceiptRequest } from '@/core/domain/reports/movements/WorkOrderFinancialReceipt/WorkOrderFinancialReceipt.types';
import { useWorkOrderFinancialReceiptReport } from '@/core/domain/reports/movements/WorkOrderFinancialReceipt/WorkOrderFinancialReceipt.hooks';
import ExternalLinkIcon from '@/components/_common/ExternalLinkIcon';
import InstallmentReceivableViewModal from '@/components/Financial/BillToReceive/InstallmentReceivableViewModal';
import { useDisclosure } from '@/hooks';
import { AccountReceivableFinancialSummary } from '@/core/domain/financial/AccountReceivable/AccountReceivable.types';
import { usePaymentStatus } from '@/core/domain/payment-gateway/transactions/transactions.hooks';
import {
  PaymentFinancialPendingStatus,
  TransactionEntityType,
} from '@/core/domain/payment-gateway/transactions/transactions.types';
import { TEFProofOfPayment } from '@/components/TEF/TEFProofOfPayment';
import { useWorkOrderPaymentReceipt } from '@/core/domain/reports/workOrderReport/workOrderReport.hooks';

const titles = {
  'domain.enums.installment_type.insurer':
    'Resumo financeiro dos valores referentes a seguradora:',
  'domain.enums.installment_type.customer':
    'Resumo financeiro dos valores registrados particular:',
};

const installmentStatus: Record<string, { color: string; label: string }> = {
  OPEN: {
    color: 'blue',
    label: 'Aberto',
  },
  SETTLED: {
    color: 'green',
    label: 'Liquidado',
  },
  CANCELED: {
    color: 'red',
    label: 'Cancelado',
  },
};

enum TypePrintReceipt {
  customer = 'CUSTOMER',
  insurer = 'INSURER',
}

interface Props {
  loading: boolean;
  financialSummary?: AccountReceivableFinancialSummary;
}

const WorkOrderFinancialSummary: React.FC<Props> = ({
  loading,
  financialSummary,
}) => {
  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const paymentStatus = usePaymentStatus(
    { entityId: workorder?.id, entityType: TransactionEntityType.WorkOrder },
    !!workorder?.id,
  );
  const workOrderReceipt = useWorkOrderPaymentReceipt();

  const report = useReportGenerate();

  const { fileLoading, setFileLoading, setFileUnloading } = useFileLoading();

  const [installmentsCustomerSelected, setInstallmentsCustomerSelected] =
    useState<number[]>([]);
  const [installmentsInsurerSelected, setInstallmentsInsurerSelected] =
    useState<number[]>([]);

  const [installmentViewOpened, installmentViewActions] = useDisclosure();
  const [installmentViewSelected, setInstallmentViewSelected] = useState<{
    installmentId: number;
    accountReceivableId: number;
  }>();

  const generateReport = useWorkOrderFinancialReceiptReport();

  const handleGenerateReport = async (
    type: TypePrintReceipt,
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);

    try {
      if (workorder) {
        const obj: WorkOrderFinancialReceiptRequest = {
          format: 'PDF',
          installmentReceivableIds:
            type === TypePrintReceipt.customer
              ? installmentsCustomerSelected.map((item) => item)
              : installmentsInsurerSelected.map((item) => item),
          workOrderId: workorder?.id,
        };

        const result = await generateReport.mutateAsync(obj);

        if (callback) {
          callback(result);
        }

        setInstallmentsCustomerSelected([]);
        setInstallmentsInsurerSelected([]);
      }
    } catch {}

    setFileUnloading(format);
  };

  const stoneDevices = useStoneDeviceList({
    companyId: workorder?.company.id,
    isActive: true,
  });

  const getDevice = useCallback(
    (deviceId: number) =>
      stoneDevices.data?.find((device) => device.id === deviceId),
    [stoneDevices.dataUpdatedAt],
  );

  const hasInsurer = !!financialSummary?.installmentReceivableInsurer.length;

  const isPaymentCompleted = useMemo(
    () =>
      paymentStatus.data?.status === PaymentFinancialPendingStatus.COMPLETED,
    [paymentStatus.data?.status],
  );

  return (
    <>
      {isPaymentCompleted && (
        <TEFProofOfPayment
          entityId={workorder?.id}
          generateReceipt={({ entityId, printTarget, printType }) =>
            workOrderReceipt.mutateAsync({
              format: 'PDF',
              workOrderId: entityId,
              receiptPrintTarget: printTarget,
              receiptPrintType: printType,
            })
          }
        />
      )}

      <Row gutter={[24, 24]}>
        <Col span={24} lg={hasInsurer ? 12 : 24}>
          <Card
            loading={loading}
            title={titles['domain.enums.installment_type.customer']}
            style={{ borderRadius: 10 }}
          >
            <Row gutter={[24, 24]}>
              <Col span={24} lg={12} xl={8}>
                <Statistic
                  title="Valor total do financeiro na O.S"
                  value={formatterPrice(
                    financialSummary?.totalPrivateValue || 0,
                  )}
                />
              </Col>
              <Col span={24} lg={12} xl={8}>
                <Statistic
                  title="Valor financeiro já processado"
                  value={formatterPrice(
                    financialSummary?.totalProcessedPrivateValue || 0,
                  )}
                />
              </Col>
              <Col span={24} lg={12} xl={8}>
                <Statistic
                  title="Valor financeiro não processado"
                  value={formatterPrice(
                    financialSummary?.totalUnprocessedPrivateValue || 0,
                  )}
                  valueStyle={{
                    color:
                      (financialSummary?.totalUnprocessedPrivateValue ?? 0) < 0
                        ? 'rgb(207, 74, 29)'
                        : undefined,
                  }}
                />
              </Col>
            </Row>

            <Divider />

            <Typography.Title level={5}>
              Detalhamento dos registros financeiros já processados:
            </Typography.Title>

            <Table
              dataSource={financialSummary?.installmentReceivableCustomer}
              pagination={false}
              scroll={{ x: true }}
              rowKey={(row) => row.installmentReceivableId}
              columns={[
                { dataIndex: 'code', title: 'Cód. Conta', width: 100 },
                {
                  dataIndex: 'dueDate',
                  title: 'Data de vencimento',
                  width: 160,
                  render: (value) => moment(value).format('DD/MM/YYYY'),
                },
                {
                  dataIndex: 'creationDate',
                  title: 'Data de lançamento',
                  width: 160,
                  render: (value) => moment(value).format('DD/MM/YYYY'),
                },
                {
                  dataIndex: 'value',
                  title: 'Valor($)',
                  width: 160,
                  render: (value: number) => formatterPrice(value),
                },
                {
                  dataIndex: 'situation',
                  title: 'Situação',
                  width: 100,
                  render: (status: string) => (
                    <StatusTag
                      color={installmentStatus[status].color}
                      label={installmentStatus[status].label}
                    />
                  ),
                },
                {
                  title: 'Ação',
                  align: 'right',
                  fixed: 'right',
                  width: 100,
                  render: (_, item) => (
                    <Group position="right" gap={16}>
                      {!!item?.isStoneOrder && (
                        <Popover
                          content={`Dispositivo: ${
                            getDevice(item?.stoneDeviceId)?.serialNumber
                          }`}
                        >
                          <img
                            src="/images/device-stone.png"
                            alt="Device Stone"
                            width={16}
                          />
                        </Popover>
                      )}

                      {item.bankingEntryId && (
                        <>
                          <EmployeePopoverGenerateReceipt
                            bankEntryId={item.bankingEntryId}
                            employeeId={complement?.estimator.id}
                            companyId={workorder?.company.id}
                            nature={Destination.revenue}
                          >
                            <ReceiptIcon />
                          </EmployeePopoverGenerateReceipt>

                          <Divider type="vertical" />
                        </>
                      )}

                      <EyeIcon
                        iconProps={{
                          onClick: () => {
                            setInstallmentViewSelected({
                              accountReceivableId: item.accountReceivableId,
                              installmentId: item.installmentReceivableId,
                            });

                            installmentViewActions.open();
                          },
                        }}
                      />

                      <ExternalLinkIcon
                        onClick={() =>
                          window.open(
                            `/app/financial/billToReceiver/releaseBillToReceiver/${item.accountReceivableId}/installment/${item.installmentReceivableId}`,
                            '_blank',
                          )
                        }
                      />
                    </Group>
                  ),
                },
              ]}
              rowSelection={{
                preserveSelectedRowKeys: true,
                onChange: (key) => {
                  setInstallmentsCustomerSelected(key as number[]);
                  setInstallmentsInsurerSelected([]);
                },
                selectedRowKeys: installmentsCustomerSelected.map((id) => id),
              }}
              footer={() => (
                <div style={{ display: 'flex', justifyContent: 'end' }}>
                  <Group gap={10}>
                    <Tooltip title="Ao clicar no botão 'Imprimir Recibo', será gerado um recibo contendo todas as parcelas que foram previamente marcadas nos checkboxes acima.">
                      <AntdFeatherIcon
                        size={20}
                        icon={<Info cursor="pointer" />}
                      />
                    </Tooltip>

                    <Button
                      type="primary"
                      disabled={!installmentsCustomerSelected.length}
                      loading={
                        !installmentsInsurerSelected.length && fileLoading.PDF
                      }
                      onClick={() =>
                        handleGenerateReport(
                          TypePrintReceipt.customer,
                          'PDF',
                          (result) =>
                            report.openFile({
                              result,
                              fileName: `recibo-financeiro-particular-os-${complement?.complementIdentifier}.pdf`,
                              fileType: 'application/pdf',
                            }),
                        )
                      }
                    >
                      Imprimir Recibo
                    </Button>
                  </Group>
                </div>
              )}
            />
          </Card>
        </Col>

        {hasInsurer && (
          <Col span={24} lg={12}>
            <Card
              loading={loading}
              title={titles['domain.enums.installment_type.insurer']}
              style={{ borderRadius: 10 }}
            >
              <Row gutter={[24, 24]}>
                <Col span={24} lg={12} xl={8}>
                  <Statistic
                    title="Valor total do financeiro na O.S"
                    value={formatterPrice(
                      financialSummary?.totalInsurerValue || 0,
                    )}
                  />
                </Col>
                <Col span={24} lg={12} xl={8}>
                  <Statistic
                    title="Valor financeiro já processado"
                    value={formatterPrice(
                      financialSummary?.totalProcessedInsurerValue || 0,
                    )}
                  />
                </Col>
                <Col span={24} lg={12} xl={8}>
                  <Statistic
                    title="Valor financeiro não processado"
                    value={formatterPrice(
                      financialSummary?.totalUnprocessedInsurerValue || 0,
                    )}
                    valueStyle={{
                      color:
                        (financialSummary?.totalUnprocessedInsurerValue ?? 0) <
                        0
                          ? 'rgb(207, 74, 29)'
                          : undefined,
                    }}
                  />
                </Col>
              </Row>

              <Divider />

              <Typography.Title level={5}>
                Detalhamento dos registros financeiros já processados:
              </Typography.Title>

              <Table
                dataSource={financialSummary?.installmentReceivableInsurer}
                pagination={false}
                scroll={{ x: true }}
                rowKey={(row) => row.installmentReceivableId}
                columns={[
                  { dataIndex: 'code', title: 'Cód. Conta', width: 100 },
                  {
                    dataIndex: 'dueDate',
                    title: 'Data de vencimento',
                    width: 160,
                    render: (value) => moment(value).format('DD/MM/YYYY'),
                  },
                  {
                    dataIndex: 'creationDate',
                    title: 'Data de lançamento',
                    width: 160,
                    render: (value) => moment(value).format('DD/MM/YYYY'),
                  },
                  {
                    dataIndex: 'value',
                    title: 'Valor($)',
                    width: 160,
                    render: (value: number) => formatterPrice(value),
                  },
                  {
                    dataIndex: 'situation',
                    title: 'Situação',
                    width: 100,
                    render: (status: string) => (
                      <StatusTag
                        color={installmentStatus[status].color}
                        label={installmentStatus[status].label}
                      />
                    ),
                  },
                  {
                    title: 'Ação',
                    align: 'right',
                    fixed: 'right',
                    width: 100,
                    render: (_, item) => (
                      <Group position="right" gap={16}>
                        {!!item?.isStoneOrder && (
                          <Popover
                            content={`Dispositivo: ${
                              getDevice(item?.stoneDeviceId)?.serialNumber
                            }`}
                          >
                            <img
                              src="/images/device-stone.png"
                              alt="Device Stone"
                              width={16}
                            />
                          </Popover>
                        )}

                        {item.bankingEntryId && (
                          <>
                            <EmployeePopoverGenerateReceipt
                              bankEntryId={item.bankingEntryId}
                              employeeId={complement?.estimator.id}
                              companyId={workorder?.company.id}
                            >
                              <ReceiptIcon />
                            </EmployeePopoverGenerateReceipt>

                            <Divider type="vertical" />
                          </>
                        )}

                        <EyeIcon
                          iconProps={{
                            onClick: () => {
                              setInstallmentViewSelected({
                                accountReceivableId: item.accountReceivableId,
                                installmentId: item.installmentReceivableId,
                              });

                              installmentViewActions.open();
                            },
                          }}
                        />

                        <ExternalLinkIcon
                          onClick={() =>
                            window.open(
                              `/app/financial/billToReceiver/releaseBillToReceiver/${item.accountReceivableId}/installment/${item.installmentReceivableId}`,
                              '_blank',
                            )
                          }
                        />
                      </Group>
                    ),
                  },
                ]}
                rowSelection={{
                  preserveSelectedRowKeys: true,
                  onChange: (key) => {
                    setInstallmentsInsurerSelected(key as number[]);
                    setInstallmentsCustomerSelected([]);
                  },
                  selectedRowKeys: installmentsInsurerSelected.map((id) => id),
                }}
                footer={() => (
                  <div style={{ display: 'flex', justifyContent: 'end' }}>
                    <Group gap={10}>
                      <Tooltip title="Ao clicar no botão 'Imprimir Recibo', será gerado um recibo contendo todas as parcelas que foram previamente marcadas nos checkboxes acima.">
                        <AntdFeatherIcon
                          size={20}
                          icon={<Info cursor="pointer" />}
                        />
                      </Tooltip>

                      <Button
                        type="primary"
                        disabled={!installmentsInsurerSelected.length}
                        loading={
                          !installmentsCustomerSelected.length &&
                          fileLoading.PDF
                        }
                        onClick={() =>
                          handleGenerateReport(
                            TypePrintReceipt.insurer,
                            'PDF',
                            (result) =>
                              report.openFile({
                                result,
                                fileName: `recibo-financeiro-seguradora-os-${complement?.complementIdentifier}.pdf`,
                                fileType: 'application/pdf',
                              }),
                          )
                        }
                      >
                        Imprimir Recibo
                      </Button>
                    </Group>
                  </div>
                )}
              />
            </Card>
          </Col>
        )}
      </Row>

      <InstallmentReceivableViewModal
        accountReceivableId={installmentViewSelected?.accountReceivableId}
        installmentId={installmentViewSelected?.installmentId}
        open={installmentViewOpened}
        onCancel={() => {
          setInstallmentViewSelected(undefined);
          installmentViewActions.close();
        }}
      />
    </>
  );
};

export default WorkOrderFinancialSummary;
